/**
 * The risk standard the Watchlist assesses against: ISO 31000 as MojoPSA
 * implements it (docs/research/2026-10-mojopsa-risk-model.md, roadmap D117).
 *
 * The contract (`../schemas/projectRisks.ts`, mirrored in mju-aioe) carries
 * the shapes — a template, an assessment, controls, the project's risk
 * settings. This file is what they mean: the built-in ISO 31000 5×5 template
 * with MojoPSA's scales, thresholds and colours, how an assessment is scored
 * and banded, how a residual follows the controls, which appetite applies at
 * which level and whether an entry is above it, when a review is overdue,
 * the register's counts and its heatmap, and which specialist suits which
 * kind of entry.
 *
 * Pure and zod-free (types only from the contract), so the renderer, the
 * host, the desktop and the agents' tools share one model.
 */
// ── The built-in template ────────────────────────────────────────────────
export const ISO_31000_TEMPLATE_ID = "iso-31000-5x5";
const scale5 = (labels, descriptions) => labels.map((label, i) => ({ level: i + 1, label, description: descriptions[i] }));
const IMPACT_LABELS = ["Insignificant", "Minor", "Moderate", "Major", "Severe"];
/**
 * ISO 31000:2018, the standard 5×5 matrix: MojoPSA's seeded template, its
 * scales and their descriptions, its thresholds (Very Low 1–2, Low 3–4,
 * Moderate 5–9, High 10–16, Extreme 17–25) and colours, and its default
 * appetite (the organisation accepts up to Moderate, a project up to High).
 */
export const ISO_31000_TEMPLATE = {
    id: ISO_31000_TEMPLATE_ID,
    name: "Standard 5×5",
    standard: { name: "ISO 31000:2018", publisher: "International Organization for Standardization", version: "2018" },
    description: "Five steps of how likely and five of how bad, scored 1 to 25. Suits work at every level: the organisation, a project, a repository.",
    likelihood: scale5(["Rare", "Unlikely", "Possible", "Likely", "Almost Certain"], ["May only occur in exceptional circumstances (under 5%)", "Could occur at some time (5–20%)", "Might occur at some time (20–50%)", "Will probably occur in most circumstances (50–80%)", "Expected to occur in most circumstances (over 80%)"]),
    impact: scale5(IMPACT_LABELS, ["Minimal impact, no significant effect on objectives", "Small impact, easily absorbed by existing processes", "Noticeable impact that needs management attention", "Significant impact on key objectives", "Critical impact that threatens the objectives"]),
    dimensions: [
        { id: "overall", label: "Overall" },
        { id: "cost", label: "Cost", scale: scale5(IMPACT_LABELS, ["Budget variance under 1%", "Budget variance 1–5%", "Budget variance 5–10%", "Budget variance 10–25%", "Budget variance over 25%"]) },
        { id: "schedule", label: "Schedule", scale: scale5(IMPACT_LABELS, ["Delay under a week", "Delay of 1–2 weeks", "Delay of 2–4 weeks", "Delay of 1–3 months", "Delay over 3 months"]) },
        { id: "scope", label: "Scope", scale: scale5(IMPACT_LABELS, ["Minor scope reduction, no effect on deliverables", "Some scope reduction, minimal effect on deliverables", "Scope reduction affecting secondary deliverables", "Significant scope reduction affecting key deliverables", "Critical scope reduction, major deliverables at risk"]) },
        { id: "quality", label: "Quality", scale: scale5(IMPACT_LABELS, ["Quality deviation barely noticeable", "Minor quality issues easily addressed", "Quality issues needing remediation", "Significant defects affecting acceptance", "Critical failures, deliverables unacceptable"]) },
        { id: "security", label: "Security", scale: scale5(IMPACT_LABELS, ["Minor security concern, no data exposed", "Limited security issue, easily contained", "A breach affecting limited systems or data", "Significant breach, sensitive data compromised", "Critical breach, widespread data loss or compromise"]) },
        { id: "reputation", label: "Reputation", scale: scale5(IMPACT_LABELS, ["No reputational impact", "Minor internal concern, not seen outside", "Local attention, stakeholder concern", "National coverage, significant stakeholder impact", "International attention, major brand damage"]) },
        { id: "regulatory", label: "Regulatory", scale: scale5(IMPACT_LABELS, ["No regulatory concern", "Minor non-compliance, easily put right", "A breach that needs a formal response", "Significant breach, formal investigation", "Prosecution, loss of licence"]) },
    ],
    bands: [
        { id: "very-low", label: "Very Low", min: 1, max: 2, colour: "#7CB342", rating: "low" },
        { id: "low", label: "Low", min: 3, max: 4, colour: "#9CCC65", rating: "low" },
        { id: "moderate", label: "Moderate", min: 5, max: 9, colour: "#FBC02D", rating: "medium" },
        { id: "high", label: "High", min: 10, max: 16, colour: "#E64A19", rating: "high" },
        { id: "extreme", label: "Extreme", min: 17, max: 25, colour: "#D32F2F", rating: "critical" },
    ],
    appetite: { organisation: "moderate", project: "high", repository: "high" },
    reviewEveryDays: 30,
};
/** A smaller template for a team that wants three steps: Low, Medium, High on a 3×3. */
export const SIMPLE_3X3_TEMPLATE = {
    id: "simple-3x3",
    name: "Simple 3×3",
    description: "Three steps of likelihood and impact, for a small team or a short piece of work.",
    likelihood: [
        { level: 1, label: "Unlikely", description: "Not expected, but possible" },
        { level: 2, label: "Possible", description: "Might well happen" },
        { level: 3, label: "Likely", description: "Expected to happen" },
    ],
    impact: [
        { level: 1, label: "Minor", description: "Easily absorbed" },
        { level: 2, label: "Moderate", description: "Needs attention and some rework" },
        { level: 3, label: "Major", description: "Threatens what the work is for" },
    ],
    dimensions: [{ id: "overall", label: "Overall" }],
    bands: [
        { id: "low", label: "Low", min: 1, max: 2, colour: "#7CB342", rating: "low" },
        { id: "medium", label: "Medium", min: 3, max: 4, colour: "#FBC02D", rating: "medium" },
        { id: "high", label: "High", min: 5, max: 9, colour: "#D32F2F", rating: "high" },
    ],
    appetite: { organisation: "medium", project: "medium", repository: "medium" },
    reviewEveryDays: 30,
};
export const BUILT_IN_TEMPLATES = [ISO_31000_TEMPLATE, SIMPLE_3X3_TEMPLATE];
/** Every template a project can choose: the built-in ones, then its own (an own template with a built-in id replaces it). */
export function availableTemplates(settings) {
    const own = settings?.templates ?? [];
    return [...BUILT_IN_TEMPLATES.filter((t) => !own.some((o) => o.id === t.id)), ...own];
}
/** The template a repository ("" = the project's own entries) assesses against: its own choice, else the project's, else ISO 31000. */
export function templateFor(settings, rootId = "", templateId) {
    const all = availableTemplates(settings);
    const want = templateId ?? (rootId ? settings?.repositories?.[rootId]?.templateId : undefined) ?? settings?.templateId ?? ISO_31000_TEMPLATE_ID;
    return all.find((t) => t.id === want) ?? ISO_31000_TEMPLATE;
}
/** The template an entry's assessments are on. */
export function templateOfEntry(entry, settings) {
    return templateFor(settings, entry.rootId, entry.templateId);
}
// ── Scoring ──────────────────────────────────────────────────────────────
export const OVERALL = "overall";
const maxLevel = (steps) => Math.max(1, ...steps.map((s) => s.level));
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, Math.round(n)));
/** The dimensions of a template, Overall first (always there). */
export function dimensionsOf(t) {
    const rest = (t.dimensions ?? []).filter((d) => d.id !== OVERALL).map((d) => ({ id: d.id, label: d.label, scale: d.scale?.length ? d.scale : t.impact }));
    const overall = (t.dimensions ?? []).find((d) => d.id === OVERALL);
    return [{ id: OVERALL, label: overall?.label ?? "Overall", scale: t.impact }, ...rest];
}
/**
 * The overall impact: the highest impact over every dimension rated (as
 * MojoPSA's rollup "Max"), the Overall entry included. 0 when none is.
 */
export function overallImpact(a) {
    if (!a)
        return 0;
    const values = Object.values(a.impacts ?? {}).filter((n) => typeof n === "number" && n > 0);
    return values.length ? Math.max(...values) : 0;
}
/** The band a score falls in; the nearest band when the score is outside every range. */
export function bandFor(score, t) {
    if (!(score > 0))
        return undefined;
    const hit = t.bands.find((b) => score >= b.min && score <= b.max);
    if (hit)
        return hit;
    const sorted = [...t.bands].sort((a, b) => a.min - b.min);
    return score < sorted[0].min ? sorted[0] : sorted[sorted.length - 1];
}
/** A band's place, lowest first (0); -1 when the template has no such band. */
export function bandIndex(bandId, t) {
    return bandId === undefined ? -1 : [...t.bands].sort((a, b) => a.min - b.min).findIndex((b) => b.id === bandId);
}
export function bandById(bandId, t) {
    return bandId === undefined ? undefined : t.bands.find((b) => b.id === bandId);
}
/** The score of one cell: likelihood × impact. */
export function cellScore(likelihood, impact) {
    return likelihood * impact;
}
/** The score and band of an assessment on one dimension (Overall: the overall impact). Undefined when that dimension is not rated. */
export function dimensionScore(a, dimension, t) {
    if (!a || !(a.likelihood > 0))
        return undefined;
    const impact = dimension === OVERALL ? overallImpact(a) : (a.impacts?.[dimension] ?? 0);
    if (!(impact > 0))
        return undefined;
    const score = cellScore(a.likelihood, impact);
    const band = bandFor(score, t);
    return { impact, score, ...(band ? { band } : {}) };
}
/**
 * An assessment as it is stored: the levels kept within the template's
 * scales, and the score, band and rating derived from it. `basis` and
 * `assessedAt` as given.
 */
export function assess(input, t) {
    const likelihood = clamp(input.likelihood, 1, maxLevel(t.likelihood));
    const dims = dimensionsOf(t);
    const impacts = {};
    for (const [dim, level] of Object.entries(input.impacts ?? {})) {
        if (!(typeof level === "number" && level > 0))
            continue;
        const d = dims.find((x) => x.id === dim);
        impacts[dim] = clamp(level, 1, maxLevel(d?.scale ?? t.impact));
    }
    const out = { likelihood, impacts };
    const scored = dimensionScore(out, OVERALL, t);
    if (scored) {
        out.score = scored.score;
        if (scored.band) {
            out.band = scored.band.id;
            out.rating = scored.band.rating;
        }
    }
    if (input.basis)
        out.basis = input.basis;
    if (input.assessedAt)
        out.assessedAt = input.assessedAt;
    if (input.notes?.trim())
        out.notes = input.notes.trim().slice(0, 2000);
    return out;
}
// ── Residual follows the controls ────────────────────────────────────────
/** How many steps an effective control takes off. */
export const EFFECTIVENESS_STEPS = { "not-effective": 0, partly: 1, mostly: 2, fully: 3 };
/** Whether a control counts now: in place (or under review); a planned or retired one does not. */
export function controlCounts(c) {
    return c.status === undefined || c.status === "in-place" || c.status === "under-review";
}
/**
 * The residual the controls give: the inherent assessment with likelihood
 * lowered by the best control that prevents it (preventive or directive, or
 * one with no type) and every impact lowered by the best control that
 * limits it once it happens (detective or corrective), by its effectiveness
 * (partly 1 step, mostly 2, fully 3), never below the lowest step.
 * Undefined without an inherent assessment; the inherent itself when no
 * control counts.
 */
export function residualFromControls(inherent, controls, t, assessedAt) {
    if (!inherent)
        return undefined;
    let lowerLikelihood = 0;
    let lowerImpact = 0;
    for (const c of controls ?? []) {
        if (!controlCounts(c))
            continue;
        const steps = EFFECTIVENESS_STEPS[c.effectiveness ?? "not-effective"];
        if (c.type === "detective" || c.type === "corrective")
            lowerImpact = Math.max(lowerImpact, steps);
        else
            lowerLikelihood = Math.max(lowerLikelihood, steps);
    }
    const impacts = {};
    for (const [dim, level] of Object.entries(inherent.impacts ?? {}))
        impacts[dim] = Math.max(1, level - lowerImpact);
    return assess({ likelihood: Math.max(1, inherent.likelihood - lowerLikelihood), impacts, basis: "controls", ...(assessedAt ? { assessedAt } : {}) }, t);
}
const controlKind = (c) => `${c.type ?? "no type"}, ${EFFECTIVENESS_TEXT[c.effectiveness ?? "not-effective"].toLowerCase()}`;
/**
 * The working behind `residualFromControls`, step by step, in the same
 * order every time: the likelihood move, then the impact move, then the
 * controls that took nothing off (in the order they were recorded).
 * Undefined without an inherent assessment, as the residual is.
 */
export function explainResidual(inherent, controls, t) {
    if (!inherent)
        return undefined;
    const list = controls ?? [];
    const residual = residualFromControls(inherent, list, t);
    const best = { likelihood: undefined, impact: undefined };
    const axisOf = (c) => (c.type === "detective" || c.type === "corrective" ? "impact" : "likelihood");
    const stepsOf = (c) => EFFECTIVENESS_STEPS[c.effectiveness ?? "not-effective"];
    for (const c of list) {
        if (!controlCounts(c) || stepsOf(c) === 0)
            continue;
        const axis = axisOf(c);
        const was = best[axis];
        if (!was || stepsOf(c) > stepsOf(was))
            best[axis] = c;
    }
    const label = (scale, level) => scale.find((s) => s.level === level)?.label ?? String(level);
    const steps = [];
    const quote = (c) => `‘${c.title}’`;
    const move = (axis, from, to, scale) => {
        const c = best[axis];
        if (!c)
            return;
        const name = axis === "likelihood" ? "Likelihood" : "Impact";
        const fromLabel = label(scale, from);
        const toLabel = label(scale, to);
        const text = from === to ? `${name} stays ${fromLabel}: ${quote(c)} (${controlKind(c)}) cannot take it below the lowest step` : `${name} ${fromLabel} → ${toLabel}: ${quote(c)} (${controlKind(c)}) −${from - to}`;
        steps.push({ axis, from, to, fromLabel, toLabel, control: { id: c.id, title: c.title, ...(c.type ? { type: c.type } : {}), ...(c.effectiveness ? { effectiveness: c.effectiveness } : {}) }, text });
    };
    move("likelihood", inherent.likelihood, residual?.likelihood ?? inherent.likelihood, t.likelihood);
    move("impact", overallImpact(inherent), overallImpact(residual), t.impact);
    const notes = [];
    for (const c of list) {
        if (c === best.likelihood || c === best.impact)
            continue;
        if (c.status === "planned")
            notes.push(`${quote(c)} is planned, so it takes nothing off yet.`);
        else if (c.status === "retired")
            notes.push(`${quote(c)} is retired, so it takes nothing off.`);
        else if (stepsOf(c) === 0)
            notes.push(`${quote(c)} is not effective, so it takes nothing off.`);
        else
            notes.push(`${quote(c)} adds nothing: only the strongest control on the ${axisOf(c)} counts, ${quote(best[axisOf(c)])}.`);
    }
    const unchanged = !residual || (residual.likelihood === inherent.likelihood && overallImpact(residual) === overallImpact(inherent));
    return { noControls: list.length === 0, unchanged, steps, notes };
}
/** The assessment that stands for the entry now: its residual, else its inherent. */
export function currentAssessment(e) {
    return e.residual ?? e.inherent;
}
/**
 * The fields to write when an assessment or the controls change: the
 * assessments derived again on the template, the residual worked out from
 * the controls unless a person set it (`basis: "assessed"`), and the
 * contract's three-step likelihood and impact kept in step, so a home or a
 * view that predates templates still rates it sensibly.
 */
export function assessmentFields(next, before, t, at) {
    const inherentIn = next.inherent === undefined ? before.inherent : (next.inherent ?? undefined);
    const controls = next.controls === undefined ? before.controls : (next.controls ?? undefined);
    const inherent = inherentIn ? assess({ ...inherentIn, ...(at && next.inherent ? { assessedAt: at } : {}) }, t) : undefined;
    let residual;
    if (next.residual)
        residual = assess({ ...next.residual, basis: next.residual.basis ?? "assessed", ...(at ? { assessedAt: at } : {}) }, t);
    else if (next.residual === undefined && before.residual?.basis === "assessed")
        residual = assess(before.residual, t);
    else if ((controls?.length ?? 0) > 0)
        residual = residualFromControls(inherent, controls ?? [], t, at);
    const current = residual ?? inherent;
    // When the entry's standing band moved, the new standing assessment says where it was (a report reads up or down).
    const was = (before.residual ?? before.inherent)?.band;
    if (current) {
        delete current.was;
        if (was && current.band && was !== current.band)
            current.was = was;
        else if (current === (residual ?? inherent) && sameStanding(before, current)) {
            const kept = (before.residual ?? before.inherent)?.was;
            if (kept)
                current.was = kept;
        }
    }
    const out = {};
    if (inherent)
        out.inherent = inherent;
    if (residual)
        out.residual = residual;
    if (current)
        Object.assign(out, legacyLevels(current, t));
    return out;
}
/** Whether the standing assessment says the same as before (levels and band): a re-save that keeps its `was`. */
function sameStanding(before, now) {
    const b = before.residual ?? before.inherent;
    return !!b && b.likelihood === now.likelihood && b.band === now.band && JSON.stringify(b.impacts) === JSON.stringify(now.impacts);
}
/** The contract's three steps for a template assessment: the lower, middle and upper thirds of each scale. */
export function legacyLevels(a, t) {
    const third = (level, top) => {
        if (top <= 1)
            return "medium";
        const at = (level - 1) / (top - 1);
        return at < 1 / 3 ? "low" : at <= 2 / 3 ? "medium" : "high";
    };
    return { likelihood: third(a.likelihood, maxLevel(t.likelihood)), impact: third(Math.max(1, overallImpact(a)), maxLevel(t.impact)) };
}
/** A three-step level on a template's scale, for an entry raised before templates (low → 2, medium → 3, high → 4 on a five-step scale). */
export function levelOnScale(level, steps) {
    if (steps <= 1)
        return 1;
    const at = level === "low" ? 0.25 : level === "high" ? 0.75 : 0.5;
    return clamp(1 + at * (steps - 1), 1, steps);
}
/** An entry's assessment, or one read from its three-step likelihood and impact when it has none (marked as such). */
export function assessmentOrLegacy(e, which, t) {
    if (e.kind !== "risk")
        return undefined;
    const own = which === "residual" ? (e.residual ?? e.inherent) : e.inherent;
    if (own)
        return { assessment: own, legacy: false };
    if (!e.likelihood)
        return undefined;
    return { assessment: assess({ likelihood: levelOnScale(e.likelihood, maxLevel(t.likelihood)), impacts: { [OVERALL]: levelOnScale(e.impact, maxLevel(t.impact)) } }, t), legacy: true };
}
export const APPETITE_LEVEL_TEXT = { organisation: "Organisation", project: "Project", repository: "Repository" };
/** The most each level accepts, by band id: the settings' over the template's defaults; a repository's own over the project's. */
export function appetiteOf(settings, t, rootId = "") {
    const valid = (id) => (id && bandById(id, t) ? id : undefined);
    const top = [...t.bands].sort((a, b) => a.min - b.min).at(-1).id;
    const organisation = valid(settings?.appetite?.organisation) ?? valid(t.appetite?.organisation) ?? top;
    const project = valid(settings?.appetite?.project) ?? valid(t.appetite?.project) ?? organisation;
    // A project's own setting beats the template's default for its repositories: a repository is never looser than the project chose.
    const repository = valid(rootId ? settings?.repositories?.[rootId]?.appetite : undefined) ?? valid(settings?.appetite?.repository) ?? valid(settings?.appetite?.project) ?? valid(t.appetite?.repository) ?? project;
    return { organisation, project, repository };
}
/** The appetite an entry is held to: its repository's for a repository's entry, the project's for the project's own. */
export function appetiteForEntry(e, settings, t) {
    const a = appetiteOf(settings, t, e.rootId);
    return e.rootId ? { level: "repository", band: a.repository } : { level: "project", band: a.project };
}
/** Whether an open risk's current assessment is in a band above the appetite it is held to. */
export function aboveAppetite(e, settings, t) {
    if (e.kind !== "risk" || !OPEN.has(e.status))
        return false;
    const current = currentAssessment(e);
    if (!current?.score)
        return false;
    const band = bandFor(current.score, t);
    return bandIndex(band?.id, t) > bandIndex(appetiteForEntry(e, settings, t).band, t);
}
// ── Reviews ──────────────────────────────────────────────────────────────
const OPEN = new Set(["open", "mitigating", "in-progress", "analysed", "monitoring", "escalated"]);
const DAY_MS = 86_400_000;
function dayOf(iso) {
    const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
    return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
}
/** The date (ISO, day only) a review falls due `days` after `from`. */
export function reviewDueAfter(from, days) {
    const d = new Date(from);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) + days * DAY_MS).toISOString().slice(0, 10);
}
/** The review cadence in force: the settings', else the template's, else 30 days. */
export function reviewEveryDays(settings, t) {
    return settings?.reviewEveryDays ?? t.reviewEveryDays ?? 30;
}
/** Whether an open entry's next review date has passed (today is not overdue). */
export function reviewOverdue(e, now) {
    if (!OPEN.has(e.status))
        return false;
    const due = dayOf(e.reviewDue);
    if (due === undefined)
        return false;
    const d = new Date(now);
    return due < Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}
/** The Inbox's key for an overdue review (D104's classification: `risk-review:` is something that needs the person). */
export function riskReviewKey(projectId, uid) {
    return `risk-review:${projectId}:${uid}`;
}
/** MojoPSA's KPI cards: total, open, closed, open risks by band, unassessed — on `which` assessment. */
export function registerCounts(entries, t, settings, now, which = "residual") {
    const byBand = Object.fromEntries(t.bands.map((b) => [b.id, 0]));
    let open = 0;
    let unassessed = 0;
    let above = 0;
    let overdue = 0;
    for (const e of entries) {
        if (!OPEN.has(e.status))
            continue;
        open++;
        if (reviewOverdue(e, now))
            overdue++;
        if (e.kind !== "risk")
            continue;
        if (aboveAppetite(e, settings, t))
            above++;
        const a = assessmentOrLegacy(e, which, t);
        const band = a?.assessment.score ? bandFor(a.assessment.score, t) : undefined;
        if (band)
            byBand[band.id] = (byBand[band.id] ?? 0) + 1;
        else
            unassessed++;
    }
    return { total: entries.length, open, closed: entries.length - open, byBand, unassessed, aboveAppetite: above, reviewsOverdue: overdue };
}
/**
 * The matrix: one cell per likelihood × impact on the template, highest
 * likelihood first (the top row), with the open risks placed on `which`
 * assessment and `dimension`. A risk with no assessment is counted in
 * `unplaced`, one assessed on another template in `otherTemplate`.
 */
export function heatmap(entries, t, opts) {
    const dimension = opts.dimension ?? OVERALL;
    const dims = dimensionsOf(t);
    const impactSteps = (dims.find((d) => d.id === dimension)?.scale ?? t.impact).map((s) => s.level).sort((a, b) => a - b);
    const likelihoodSteps = t.likelihood.map((s) => s.level).sort((a, b) => b - a);
    const rows = likelihoodSteps.map((l) => impactSteps.map((i) => {
        const score = cellScore(l, i);
        const band = bandFor(score, t);
        return { likelihood: l, impact: i, score, ...(band ? { band } : {}), uids: [] };
    }));
    let unplaced = 0;
    let otherTemplate = 0;
    for (const e of entries) {
        if (e.kind !== "risk" || !OPEN.has(e.status))
            continue;
        if (opts.settings !== undefined && templateOfEntry(e, opts.settings).id !== t.id) {
            otherTemplate++;
            continue;
        }
        const a = assessmentOrLegacy(e, opts.which, t);
        const placed = a ? dimensionScore(a.assessment, dimension, t) : undefined;
        const cell = placed ? rows.find((r) => r[0]?.likelihood === a.assessment.likelihood)?.find((c) => c.impact === placed.impact) : undefined;
        if (cell)
            cell.uids.push(e.uid);
        else
            unplaced++;
    }
    return { rows, unplaced, otherTemplate };
}
// ── Bugs ─────────────────────────────────────────────────────────────────
export const SEVERITY_TEXT = { blocker: "Blocker", critical: "Critical", major: "Major", minor: "Minor", trivial: "Trivial" };
/** A bug's impact in the contract's three steps, from its severity. */
export function impactForSeverity(s) {
    return s === "blocker" || s === "critical" ? "high" : s === "major" ? "medium" : "low";
}
const SECURITY = /\b(security|secure|vulnerab|cve|auth|secret|credential|token|leak|injection|xss|csrf|privacy|pii|encrypt)/i;
const ARCHITECTURE = /\b(architect|design|coupling|scalab|performance|tech(nical)? debt|migration|platform|dependency|infrastructure)/i;
/**
 * Which role suits an entry: a security risk (by its category, its Security
 * dimension or its words) to a security reviewer; a bug to the debugger, with
 * the diagnosing-bugs skill; an architecture risk to the architect; anything
 * else to whoever plans the work.
 */
export function suggestRole(e) {
    const text = `${e.category ?? ""} ${e.title} ${e.description ?? ""}`;
    const securityRated = (currentAssessment(e)?.impacts?.security ?? 0) >= 3;
    if (/^security$/i.test(e.category ?? "") || securityRated || SECURITY.test(text))
        return { match: ["security", "secure", "appsec"], role: "Security reviewer", why: "It is about security." };
    if (e.kind === "bug")
        return { match: ["debug", "bug", "fix", "diagnos", "engineer", "developer"], role: "Debugger", skill: "diagnosing-bugs", why: "A bug: diagnose it, then fix it." };
    if (/^architecture$/i.test(e.category ?? "") || ARCHITECTURE.test(text))
        return { match: ["architect", "design"], role: "Architect", why: "It is about the design." };
    return { match: ["lead", "planner", "manager", "engineer", "developer"], role: "Engineer", why: "Whoever plans and does the work." };
}
/** Whether a specialist (its role and name) fits a suggestion. */
export function fitsSuggestion(s, who) {
    const text = `${who.role ?? ""} ${who.name ?? ""}`.toLowerCase();
    return s.match.some((m) => text.includes(m));
}
// ── Words for people ────────────────────────────────────────────────────
export const DISPOSITION_TEXT = {
    treat: { label: "Treat", hint: "Reduce it: controls that make it less likely or less costly" },
    tolerate: { label: "Tolerate", hint: "Live with it, within appetite, and keep watching" },
    transfer: { label: "Transfer", hint: "Hand it to someone better placed: a supplier, insurance, another team" },
    terminate: { label: "Terminate", hint: "Stop doing what causes it" },
};
export const CONFIDENTIALITY_TEXT = { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" };
export const CONTROL_TYPE_TEXT = { preventive: "Preventive", detective: "Detective", corrective: "Corrective", directive: "Directive" };
export const EFFECTIVENESS_TEXT = { "not-effective": "Not effective", partly: "Partly effective", mostly: "Mostly effective", fully: "Fully effective" };
export const CONTROL_STATUS_TEXT = { planned: "Planned", "in-place": "In place", "under-review": "Under review", retired: "Retired" };
/** A status in words for its kind: a risk's `open` reads "Identified" (the word the risk model this follows uses). */
export function statusWords(kind, status, text) {
    return kind === "risk" && status === "open" ? "Identified" : text[status];
}
/** The next control id in an entry: `c<n+1>`. */
export function nextControlId(controls) {
    const n = Math.max(0, ...(controls ?? []).map((c) => Number(/^c(\d+)$/.exec(c.id)?.[1] ?? 0)));
    return `c${n + 1}`;
}
/** The rating word of a band id on a template. */
export function ratingOfBand(bandId, t) {
    return bandById(bandId, t)?.rating;
}
//# sourceMappingURL=standard.js.map